// Role-based permissions — ported from vanilla PERMISSIONS + can() (public/app/index.html L6023).
// Roles: admin | project_manager | field_crew
// PMs start with zero capabilities; admins grant each via pmCan* toggles in opts.

import type { Role, Opts } from '../types'

export type Permission =
  | 'canCreateProject' | 'canEditProject' | 'canDeleteProject'
  | 'canCreateEstimate' | 'canEditEstimate' | 'canApproveEstimate'
  | 'canCreateExpense' | 'canEditExpense' | 'canDeleteExpense'
  | 'canCreatePayment' | 'canEditPayment'
  | 'canCreateSub' | 'canEditSub' | 'canDeleteSub'
  | 'canCreateClient' | 'canEditClient' | 'canDeleteClient'
  | 'canCreateVendor' | 'canEditVendor' | 'canDeleteVendor'
  | 'canManageSchedule' | 'canManageTeam'
  | 'canEditOptions' | 'canEditBizProfile'
  | 'canSeeFinancials' | 'canExportData'
  | 'canAssignCrew' | 'canApproveCrewHours'
  | 'canSendPortalLinks' | 'canManageBids' | 'canPushBidToEstimate'
  | 'canLogHours'
  | 'canCreateChangeOrder' | 'canApproveChangeOrder' | 'canDeleteChangeOrder'
  | 'canCreateReminder'
  | 'canUploadAttachment'

type PermissionMap = Record<Permission, boolean> & { tabs: string[] }

const ADMIN: PermissionMap = {
  tabs: ['overview','projects','estimates','subs','vendors','clients','expenses','bidroom','payments','reports','options'],
  canCreateProject: true,  canEditProject: true,  canDeleteProject: true,
  canCreateEstimate: true, canEditEstimate: true,  canApproveEstimate: true,
  canCreateExpense: true,  canEditExpense: true,   canDeleteExpense: true,
  canCreatePayment: true,  canEditPayment: true,
  canCreateSub: true,      canEditSub: true,       canDeleteSub: true,
  canCreateClient: true,   canEditClient: true,    canDeleteClient: true,
  canCreateVendor: true,   canEditVendor: true,    canDeleteVendor: true,
  canManageSchedule: true, canManageTeam: true,
  canEditOptions: true,    canEditBizProfile: true,
  canSeeFinancials: true,  canExportData: true,
  canAssignCrew: true,     canApproveCrewHours: true,
  canSendPortalLinks: true, canManageBids: true,   canPushBidToEstimate: true,
  canLogHours: true,
  canCreateChangeOrder: true, canApproveChangeOrder: true, canDeleteChangeOrder: true,
  canCreateReminder: true,
  canUploadAttachment: true,
}

const PM_BASE: PermissionMap = {
  tabs: ['overview','projects','estimates','subs','vendors','clients','expenses','bidroom','payments','reports','options'],
  canCreateProject: false, canEditProject: false,  canDeleteProject: false,
  canCreateEstimate: false,canEditEstimate: false,  canApproveEstimate: false,
  canCreateExpense: false, canEditExpense: false,   canDeleteExpense: false,
  canCreatePayment: false, canEditPayment: false,
  canCreateSub: false,     canEditSub: false,       canDeleteSub: false,
  canCreateClient: false,  canEditClient: false,    canDeleteClient: false,
  canCreateVendor: false,  canEditVendor: false,    canDeleteVendor: false,
  canManageSchedule: false,canManageTeam: false,
  canEditOptions: false,   canEditBizProfile: false,
  canSeeFinancials: false, canExportData: false,
  canAssignCrew: false,    canApproveCrewHours: false,
  canSendPortalLinks: false,canManageBids: false,  canPushBidToEstimate: false,
  canLogHours: false,
  canCreateChangeOrder: false, canApproveChangeOrder: false, canDeleteChangeOrder: false,
  canCreateReminder: false,
  canUploadAttachment: false,
}

const CREW: PermissionMap = {
  tabs: ['overview','projects','timelog','options'],
  canCreateProject: false, canEditProject: false,  canDeleteProject: false,
  canCreateEstimate: false,canEditEstimate: false,  canApproveEstimate: false,
  canCreateExpense: false, canEditExpense: false,   canDeleteExpense: false,
  canCreatePayment: false, canEditPayment: false,
  canCreateSub: false,     canEditSub: false,       canDeleteSub: false,
  canCreateClient: false,  canEditClient: false,    canDeleteClient: false,
  canCreateVendor: false,  canEditVendor: false,    canDeleteVendor: false,
  canManageSchedule: false,canManageTeam: false,
  canEditOptions: false,   canEditBizProfile: false,
  canSeeFinancials: false, canExportData: false,
  canAssignCrew: false,    canApproveCrewHours: false,
  canSendPortalLinks: false,canManageBids: false,  canPushBidToEstimate: false,
  canLogHours: true,
  canCreateChangeOrder: false, canApproveChangeOrder: false, canDeleteChangeOrder: false,
  canCreateReminder: false,
  canUploadAttachment: false,
}

// Maps PM permission keys → opts keys that admins can grant.
const PM_OVERRIDES: Partial<Record<Permission, keyof Opts>> = {
  canApproveEstimate:  'pmCanConvertEstimate',
  canCreateEstimate:   'pmCanCreateEstimate',
  canEditEstimate:     'pmCanEditEstimate',
  canCreateProject:    'pmCanCreateProject',
  canDeleteProject:    'pmCanDeleteProject',
  canCreatePayment:    'pmCanRecordPayment',
  canEditPayment:      'pmCanEditPayment',
  canCreateClient:     'pmCanAddClient',
  canEditClient:       'pmCanEditClient',
  canCreateSub:        'pmCanAddSub',
  canEditSub:          'pmCanEditSub',
  canCreateVendor:     'pmCanAddVendor',
  canEditVendor:       'pmCanEditVendor',
  canExportData:       'pmCanExportData',
  canSeeFinancials:    'pmCanSeeFinancials',
  canAssignCrew:       'pmCanAssignCrew',
  canApproveCrewHours: 'pmCanApproveCrewHours',
  canEditProject:      'pmCanEditProject',
  canCreateExpense:    'pmCanCreateExpense',
  canEditExpense:      'pmCanEditExpense',
  canDeleteExpense:    'pmCanDeleteExpense',
  canSendPortalLinks:  'pmCanSendPortalLinks',
  canManageBids:       'pmCanManageBids',
  canPushBidToEstimate:'pmCanPushBidToEstimate',
  canManageSchedule:   'pmCanManageSchedule',
  canDeleteSub:        'pmCanDeleteSub',
  canDeleteClient:     'pmCanDeleteClient',
  canDeleteVendor:     'pmCanDeleteVendor',
  canCreateChangeOrder: 'pmCanCreateChangeOrder',
  canApproveChangeOrder: 'pmCanApproveChangeOrder',
  canCreateReminder:     'pmCanCreateReminder',
  canUploadAttachment:   'pmCanUploadAttachment',
}

/** Derives role from opts — mirrors vanilla getRole().
 *
 *  V1 parity: elevated partner PMs operate as `admin` against the owner's
 *  appstate (public/app/index.html L5909–5912). If the current user's email
 *  is in the team doc's `elevatedMembers`, force role = 'admin' regardless
 *  of `teamRole`. Callers with no team context behave as before.
 */
export function getRole(
  opts: Opts,
  ctx?: { userEmail?: string | null; elevatedMembers?: string[] | null | undefined },
): Role {
  if (ctx && ctx.userEmail && Array.isArray(ctx.elevatedMembers) && ctx.elevatedMembers.length) {
    const email = ctx.userEmail.toLowerCase()
    const list = ctx.elevatedMembers.map((e) => (e || '').toLowerCase())
    if (list.includes(email)) return 'admin'
  }
  if (opts.teamCode && opts.teamRole) return opts.teamRole as Role
  return (opts.role as Role | undefined) ?? 'admin'
}

/** Returns the permission set for a given role + opts. */
function getPermissions(role: Role): PermissionMap {
  if (role === 'admin') return ADMIN
  if (role === 'project_manager') return PM_BASE
  return CREW
}

/** Mirrors vanilla can(perm) — checks role-based permissions + PM overrides. */
export function can(perm: Permission, role: Role, opts: Opts): boolean {
  const base = getPermissions(role)
  if (role === 'project_manager') {
    const optsKey = PM_OVERRIDES[perm]
    if (optsKey) return !!(opts as Record<string, unknown>)[optsKey]
  }
  return base[perm] ?? false
}

/** Returns the tabs visible for a given role + opts combination. */
export function getVisibleTabs(role: Role, opts: Opts): string[] {
  const base = [...getPermissions(role).tabs]
  let tabs = base

  // Inject portfolio after overview for investors
  if (
    opts.companyType === 'investor' &&
    can('canSeeFinancials', role, opts) &&
    opts.showPortfolio !== false &&
    !tabs.includes('portfolio')
  ) {
    const oi = tabs.indexOf('overview')
    tabs.splice(oi + 1, 0, 'portfolio')
  }

  // Remove feature-flagged tabs
  if (!opts.showSubs)              tabs = tabs.filter((t) => t !== 'subs')
  if (!opts.showVendors)           tabs = tabs.filter((t) => t !== 'vendors')
  if (opts.showBidRoom === false)  tabs = tabs.filter((t) => t !== 'bidroom')

  return tabs
}
